import { useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Markdown } from 'tiptap-markdown'
import { EditorToolbar } from './EditorToolbar'

interface MarkdownEditorProps {
  value: string
  onChange: (markdown: string) => void
  onBlur?: () => void
  // Accepted for API parity with the plain textarea. TipTap renders
  // placeholders only via @tiptap/extension-placeholder (not in scope here),
  // so this is intentionally not displayed.
  placeholder?: string
}

export function MarkdownEditor({
  value,
  onChange,
  onBlur,
}: MarkdownEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
      }),
      Link.configure({ openOnClick: false }),
      Image,
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.storage.markdown.getMarkdown())
    },
    onBlur: () => {
      onBlur?.()
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[120px] rounded-md border border-input bg-background px-3 py-2',
      },
    },
  })

  // Controlled sync: only push external value changes into the editor.
  // setContent(value, false) keeps emitUpdate off so hydration does not fire
  // onChange (avoiding false-dirty form state and an onChange->setContent loop).
  useEffect(() => {
    if (!editor) return
    const current = editor.storage.markdown.getMarkdown()
    if (current !== value) {
      editor.commands.setContent(value, false)
    }
  }, [value, editor])

  if (!editor) return null

  return (
    <div>
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  )
}
